import React from "react";
import { graphql, useFragment } from "react-relay";
import type { ActionMeta, OnChangeValue } from "react-select";
import type { StylesConfig } from "react-select";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faChevronDown, faHashtag, faLink, faLockAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import { uniq } from "lodash";
import { commitMutation } from "relay-runtime";

import { useToggle } from "~/lib/hooks";
import { warning } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { copyToClipboard, useGetURL } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import { Button, Tooltip } from "~/ui";
import { ButtonLink, Modal, SimpleTabs } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import { __globalModalContext, type ModalProps } from "~/ui/modals";
import Selector from "~/ui/selectors/Selector";
import { condensedStyles } from "~/ui/selectors/SelectorUtils";
import toast from "~/ui/toast";

import { BillingIdentity } from "../billing";
import EmailListInput, { type Contact } from "../common/EmailListInput";
import { useVisibilityChangeMutation as useClipVisibilityChangeMutation, useShareClipMutation } from "./ClipTrimmer";
import ContactChannelSelector, { type ContactChannelOption } from "./ContactChannelSelector";

import type {
	ShareClipModal_clip$data as Clip,
	ShareClipModal_clip$key,
	MeetingRecordingClipVisibility as Visibility,
} from "~/__generated__/ShareClipModal_clip.graphql";
import type {
	ShareClipModal_postedChannels$key,
	ShareClipModal_postedChannels$data as ShareClipModalChannels,
} from "~/__generated__/ShareClipModal_postedChannels.graphql";
import type { ShareClipModalQuery, ShareClipModalQuery$data } from "~/__generated__/ShareClipModalQuery.graphql";
import type { ShareClipModalRemoveMutation } from "~/__generated__/ShareClipModalRemoveMutation.graphql";
import type { ShareClipModalShareMutation } from "~/__generated__/ShareClipModalShareMutation.graphql";

import styles from "./css/ShareClipModal.module.css";

const TAB_VALUES = ["Share", "Send"] as const;
type TabType = (typeof TAB_VALUES)[number];

const VISIBILITY_VALUES: Visibility[] = ["PRIVATE", "WORKSPACE", "PUBLIC"];
const VISIBILITY_LABELS = {
	PRIVATE: "Restricted access",
	WORKSPACE: "Anyone in my workspace",
	PUBLIC: "Anyone with the link",
};

export const VISIBILITY_OPTIONS = VISIBILITY_VALUES.map(value => ({
	value,
	label: VISIBILITY_LABELS[value],
	isDisabled: false,
}));

export type VisibilityOption = { value: Visibility; label: string; isDisabled: boolean };

export const SELECTOR_STYLES: StylesConfig<VisibilityOption> = {
	control: (base, state) => ({
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		...(condensedStyles.control ? condensedStyles.control(base, state as any) : base),
		backgroundColor: `var(--${
			state.selectProps.menuIsOpen ? "veryMutedRoyal" : state.isFocused ? "veryMutedGrey" : "initial"
		})`,
		font: "var(--paragraph-4)",
	}),
};

const clipFragment = graphql`
	fragment ShareClipModal_clip on MeetingRecordingClip {
		id
		visibility
		ownerAccount {
			allowPublicRecap
		}
		...utils_useGetURL_recording
		...ShareClipModal_postedChannels
	}
`;

const modalQuery = graphql`
	query ShareClipModalQuery($clipId: ID!) {
		viewer {
			...BillingIdentityFeaturesPaywall_viewer
		}

		node(id: $clipId) {
			__typename
			... on MeetingRecordingClip {
				...ShareClipModal_clip
			}
		}
	}
`;

const shareToChannelsMutation = graphql`
	mutation ShareClipModalShareMutation($clipId: ID, $channelIds: [ID!]!) {
		shareToChannels(clipId: $clipId, channelIds: $channelIds) {
			clip {
				...ShareClipModal_postedChannels
			}

			channels {
				...ShareMeetingModal_Channel
			}
		}
	}
`;

const removeFromChannelMutation = graphql`
	mutation ShareClipModalRemoveMutation($recordingOrClipId: ID, $noteId: ID, $channelId: ID!) {
		removeFromChannel(recordingOrClipId: $recordingOrClipId, noteId: $noteId, channelId: $channelId) {
			recordingOrClip {
				...ShareClipModal_postedChannels
			}

			note {
				...ShareMeetingModal_postedChannels
			}

			channel {
				...ShareMeetingModal_Channel
			}
		}
	}
`;

const postedChannelsFragment = graphql`
	fragment ShareClipModal_postedChannels on MeetingRecordingInterface {
		postedInChannels {
			id
			name
			isPrivate

			members {
				id
			}
		}
	}
`;

type ChannelType = ShareClipModalChannels["postedInChannels"][number];

function Channel({
	channel,
	clipId,
	noteId,
	readonly = false,
}: {
	channel: ChannelType;
	clipId?: string;
	noteId?: string;
	readonly?: boolean;
}) {
	const [loading, setLoading] = React.useState(false);

	const onRemove = React.useCallback(async () => {
		if (loading) return;

		const confirmText =
			`Are you sure you want to remove "${channel.name}"?` +
			` Members from that channel may not be able to access this ${clipId ? "clip" : "meeting"} anymore.`;

		const result = await warning(confirmText, { yesText: "Remove", title: "Are you sure?" });

		if (typeof result !== "boolean" || !result) {
			return;
		}

		setLoading(true);
		commitMutation<ShareClipModalRemoveMutation>(environment, {
			mutation: removeFromChannelMutation,
			variables: {
				recordingOrClipId: clipId,
				noteId,
				channelId: channel.id,
			},
			onCompleted: ({ removeFromChannel }, errors) => {
				if (errors?.length || !removeFromChannel) {
					toast.error(errors?.[0]?.message || "Unable to remove from channel");
				} else {
					toast.success("Removed from channel");
				}
				setLoading(false);
			},
		});
	}, [channel.id, channel.name, loading, clipId, noteId]);

	return (
		<div className={styles.channel}>
			<div className={styles.iconContainer}>
				<FontAwesomeIcon fixedWidth icon={channel.isPrivate ? faLockAlt : faHashtag} className={styles.icon} />
			</div>
			<p className={styles.name}>{channel.name}</p>
			{readonly ? (
				<p className={styles.accessLabel}>Can view </p>
			) : (
				<Menu>
					<Menu.Trigger>
						<Button size="slim" className={styles.menuTrigger}>
							Can view <FontAwesomeIcon icon={faCaretDown} className={styles.icon} />
						</Button>
					</Menu.Trigger>
					<Menu.Content>
						<Menu.RadioGroup value="view">
							<Menu.RadioItem value="view" className={styles.radioItem}>
								Can view
							</Menu.RadioItem>
						</Menu.RadioGroup>
						<Menu.Separator />
						<Menu.Item destructive onSelect={onRemove}>
							Remove…
						</Menu.Item>
					</Menu.Content>
				</Menu>
			)}
		</div>
	);
}

export function PostedChannelList({
	channels,
	clipId,
	noteId,
	initialMaxNumChannels = 3,
	readonly = false,
}: {
	channels: readonly ChannelType[];
	clipId?: string;
	noteId?: string;
	initialMaxNumChannels?: number;
	readonly?: boolean;
}) {
	const [showAll, toggleShowAll] = useToggle(false);

	const sortedChannels = React.useMemo(() => [...channels].sort((a, b) => a.name.localeCompare(b.name)), [channels]);

	const totalChannels = sortedChannels.length;

	const numHiddenChannels =
		showAll || totalChannels <= initialMaxNumChannels ? 0 : totalChannels - initialMaxNumChannels;

	const visibleChannels = numHiddenChannels === 0 ? sortedChannels : sortedChannels.slice(0, initialMaxNumChannels);

	const tooltipContent = clipId
		? "Channel members have view access to this Clip"
		: "Channel members have view access to the Note and the Recap for this meeting";

	const tooltipWidth = clipId ? "10.5em" : "13.75em";

	return (
		<div className={styles.accessListContainer}>
			<p className={styles.label}>
				Channel access:
				<Tooltip content={tooltipContent} placement="top" maxWidth={tooltipWidth} className={styles.tooltip}>
					<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
				</Tooltip>
			</p>

			{visibleChannels.map(channel => (
				<Channel key={channel.id} channel={channel} clipId={clipId} noteId={noteId} readonly={readonly} />
			))}

			{totalChannels === 0 && (
				<p className={styles.emptyMessage}>
					This {clipId ? "clip" : "meeting"} has not yet been shared with any channels.
				</p>
			)}

			{numHiddenChannels > 0 && (
				<ButtonLink className={styles.viewMoreButton} onClick={toggleShowAll}>
					<FontAwesomeIcon icon={faChevronDown} className={styles.icon} />
					View more ({numHiddenChannels})
				</ButtonLink>
			)}
		</div>
	);
}

function NoPublicWarningBanner() {
	return (
		<div className={styles.warningBanner}>
			<FontAwesomeIcon icon={faInfoCircle} className={styles.icon} />
			<p className={styles.text}>
				<b>Your workspace doesn&apos;t allow public access to Clips,</b> some emails above won&apos;t have access to the
				Clip because they are external to your workspace.
			</p>
		</div>
	);
}

function PublicWarningBanner() {
	return (
		<div className={styles.warningBanner}>
			<FontAwesomeIcon icon={faInfoCircle} className={styles.icon} />
			<p className={styles.text}>
				<b>Clip access will be updated to &quot;Anyone with the link&quot; when you send</b>, because some emails
				entered above are external to your workspace.
			</p>
		</div>
	);
}

function WorkspaceWarningBanner() {
	return (
		<div className={styles.warningBanner}>
			<FontAwesomeIcon icon={faInfoCircle} className={styles.icon} />
			<p className={styles.text}>
				<b>Clip access will be updated to &quot;Anyone in my workspace&quot; when you send</b>, because some emails
				entered above do not have access to this meeting.
			</p>
		</div>
	);
}

function ModalFooter({ clip, visibilityChangingTo }: { clip: Clip; visibilityChangingTo?: Visibility }) {
	const getClipURL = useGetURL(clip);

	const copyLink = React.useCallback(() => {
		if (!getClipURL) return;
		copyToClipboard(getClipURL());
	}, [getClipURL]);

	const [changeClipVisibility, mutationInProgress] = useClipVisibilityChangeMutation();
	const onChangeVisibility = React.useCallback(
		async (option: VisibilityOption | null) => {
			if (!option || mutationInProgress) return;

			try {
				await changeClipVisibility(clip.id, option.value);
				toast.success("Visibility updated successfully");
			} catch (e) {
				toast.error("Something went wrong, please try again later", { title: "Failed to change visibility" });
			}
		},
		[clip.id, changeClipVisibility, mutationInProgress],
	);

	const visibilityIsPrivate = clip.visibility === "PRIVATE";

	return (
		<Modal.Footer className={styles.modalFooter}>
			<div className={styles.visibilityContainer}>
				{visibilityChangingTo && <FontAwesomeIcon icon={faInfoCircle} className={styles.warningIcon} />}
				<Tooltip
					content="Anyone with meeting access and shared channels"
					placement="top"
					maxWidth="10.5em"
					className={styles.tooltip}
					disabled={!visibilityIsPrivate}
				>
					<div>
						<Selector<VisibilityOption>
							className={styles.selector}
							condensed
							options={VISIBILITY_OPTIONS.map(option => ({
								...option,
								isDisabled: option.value === "PUBLIC" && !clip.ownerAccount.allowPublicRecap,
							}))}
							value={VISIBILITY_OPTIONS.find(opt => opt.value === clip.visibility)}
							onChange={onChangeVisibility}
							styles={SELECTOR_STYLES}
							isDisabled={mutationInProgress}
							menuPlacement="auto"
						/>
					</div>
				</Tooltip>

				<p className={styles.text}>{visibilityIsPrivate ? "" : "can view"}</p>

				<ButtonLink className={styles.copyLink} onClick={copyLink}>
					<FontAwesomeIcon icon={faLink} className={styles.icon} /> Copy link
				</ButtonLink>
			</div>

			{visibilityChangingTo === "WORKSPACE" && <WorkspaceWarningBanner />}
			{visibilityChangingTo === "PUBLIC" &&
				(clip.ownerAccount.allowPublicRecap ? <PublicWarningBanner /> : <NoPublicWarningBanner />)}
		</Modal.Footer>
	);
}

type ShareClipModalProps = ModalProps<unknown> & {
	clip: ShareClipModal_clip$key;
	viewer: ShareClipModalQuery$data["viewer"];
	initialTab?: TabType;
};

function ShareClipModal({ close, clip: clipKey, viewer, initialTab = TAB_VALUES[0] }: ShareClipModalProps) {
	const clip = useFragment<ShareClipModal_clip$key>(clipFragment, clipKey);
	const { postedInChannels } = useFragment<ShareClipModal_postedChannels$key>(postedChannelsFragment, clip);

	const [activeTab, setActiveTab] = React.useState<TabType>(initialTab);
	const [loading, setLoading] = React.useState(false);

	// Share tab
	const [selectedChannels, setSelectedChannels] = React.useState<readonly ContactChannelOption[]>([]);
	const onChannelsChange = React.useCallback((value: OnChangeValue<ContactChannelOption, boolean>) => {
		if (value) {
			if (value instanceof Array) {
				setSelectedChannels(value);
			} else {
				setSelectedChannels([value]);
			}
		} else {
			setSelectedChannels([]);
		}
	}, []);

	const onShare = React.useCallback(() => {
		if (loading) return;

		setLoading(true);
		const channelIds = selectedChannels.map(c => c.value);

		commitMutation<ShareClipModalShareMutation>(environment, {
			mutation: shareToChannelsMutation,
			variables: { clipId: clip.id, channelIds },
			onCompleted: ({ shareToChannels }, errors) => {
				if (errors?.length || !shareToChannels) {
					toast.error(errors?.[0]?.message || `Unable to share in channel${channelIds.length > 1 ? "s" : ""}`);
				} else {
					toast.success(`Shared to channel${channelIds.length > 1 ? "s" : ""}`);
					setSelectedChannels([]);
				}
				setLoading(false);
			},
		});
	}, [loading, clip.id, selectedChannels]);

	const filterOption = React.useCallback(
		(option: ContactChannelOption) => {
			const optionIsListed = postedInChannels.some(c => c.id === option.value);

			return !optionIsListed;
		},
		[postedInChannels],
	);

	const selectNewChannel = React.useCallback((channel: { id: string; name: string; isPrivate: boolean }) => {
		setSelectedChannels(prevSelections => [
			...prevSelections,
			{
				label: channel.name,
				value: channel.id,
				icon: channel.isPrivate ? faLock : faHashtag,
			},
		]);
	}, []);

	const shareTabContent = (
		<>
			<div className={styles.inputContainer}>
				<ContactChannelSelector
					placeholder="Add channels…"
					value={selectedChannels}
					onChange={onChannelsChange}
					filterOption={filterOption}
					afterNewChannelCreated={selectNewChannel}
					defaultOptions
					isMulti
					skipContacts
					autoFocus
					userIdsToExclude={[]}
				/>
				<BillingIdentity feature="sharing_channels" viewer={viewer} iconInheritColor>
					{(extraProps, icon, paywalled) => (
						<Button
							variant={paywalled ? "billing" : "primary"}
							disabled={selectedChannels.length === 0 || loading}
							onClick={onShare}
							className={styles.button}
							{...extraProps}
						>
							Share {icon}
						</Button>
					)}
				</BillingIdentity>
			</div>

			<PostedChannelList channels={postedInChannels} clipId={clip.id} />
		</>
	);

	// Send tab
	const userIdsWithAccess = React.useMemo(
		() => uniq(postedInChannels.map(channel => channel.members.map(member => member.id)).flat()),
		[postedInChannels],
	);

	const [visibilityChangingTo, setVisibilityChangingTo] = React.useState<Visibility | undefined>(undefined);
	const [contacts, setContacts] = React.useState<readonly Contact[]>([]);

	const onClearContacts = React.useCallback(() => {
		setContacts([]);
		setVisibilityChangingTo(undefined);
	}, []);

	const onChangeContacts = React.useCallback(
		(value: OnChangeValue<Contact, true>, meta: ActionMeta<Contact>) => {
			if (["select-option", "create-option"].includes(meta.action) && meta.option) {
				const newContact = meta.option;

				if (["PRIVATE", "WORKSPACE"].includes(clip.visibility) && (newContact.isExternal || newContact.isGuest)) {
					setVisibilityChangingTo("PUBLIC");
				} else if (
					clip.visibility === "PRIVATE" &&
					!visibilityChangingTo &&
					!userIdsWithAccess.includes(newContact.user?.id ?? "")
				) {
					setVisibilityChangingTo("WORKSPACE");
				}
			} else if (meta.action === "remove-value") {
				const thereAreExternalContacts = value.some(contact => contact.isExternal || contact.isGuest);
				const thereAreUsersWithoutAccess = value.some(contact => !userIdsWithAccess.includes(contact.user?.id ?? ""));

				if (visibilityChangingTo === "PUBLIC" && !thereAreExternalContacts) {
					if (clip.visibility === "PRIVATE" && thereAreUsersWithoutAccess) {
						setVisibilityChangingTo("WORKSPACE");
					} else {
						setVisibilityChangingTo(undefined);
					}
				} else if (visibilityChangingTo === "WORKSPACE" && !thereAreUsersWithoutAccess) {
					setVisibilityChangingTo(undefined);
				}
			}
			setContacts(value);
		},
		[userIdsWithAccess, clip.visibility, visibilityChangingTo],
	);

	const [sendClip, sendingClip] = useShareClipMutation();
	const [changeClipVisibility, changingClipVisibilty] = useClipVisibilityChangeMutation();

	const onSend = React.useCallback(async () => {
		if (loading || sendingClip || changingClipVisibilty) return;

		const emails = contacts.map(contact => contact.email);

		try {
			if (visibilityChangingTo) {
				// We need to change visiblity before sending emails
				// ... and if it's public, we need to make sure it's allowed.
				// But we still want to send the emails, so we don't return here if it's not allowed.

				if (visibilityChangingTo !== "PUBLIC" || clip.ownerAccount.allowPublicRecap) {
					changeClipVisibility(clip.id, visibilityChangingTo);
				}
			}

			await sendClip(clip.id, emails);
			toast.success("Clip shared successfully");
			onClearContacts();
		} catch (e) {
			Sentry.captureException(e);
			toast.error("Something went wrong, please try again later", { title: "Failed to share clip" });
		}
	}, [
		loading,
		clip.id,
		clip.ownerAccount.allowPublicRecap,
		contacts,
		sendClip,
		sendingClip,
		onClearContacts,
		visibilityChangingTo,
		changeClipVisibility,
		changingClipVisibilty,
	]);

	const sendTabContent = (
		<>
			<div className={styles.labelContainer}>
				<label className={styles.label}>Send clip via email to:</label>
				<ButtonLink onClick={onClearContacts} className={styles.clearButton}>
					Clear
				</ButtonLink>
			</div>

			<div className={styles.inputContainer}>
				<EmailListInput
					isMulti
					autoFocus
					value={contacts}
					onChange={onChangeContacts}
					placeholder="Add one or more teammates by email…"
				/>
				<Button
					variant="primary"
					disabled={contacts.length === 0 || loading || sendingClip || changingClipVisibilty}
					onClick={onSend}
					className={styles.button}
				>
					Send
				</Button>
			</div>
		</>
	);

	return (
		<Modal onClose={close} width="500px">
			<Modal.Header className={styles.modalHeader}>
				<SimpleTabs tabs={TAB_VALUES} tab={activeTab} setTab={setActiveTab} className={styles.tabs} />
				<Modal.HeaderActions onClose={close} className={styles.modalHeaderActions} />
			</Modal.Header>

			<Modal.Body className={styles.modalBody}>
				{activeTab === TAB_VALUES[0] ? shareTabContent : sendTabContent}
			</Modal.Body>

			<ModalFooter clip={clip} visibilityChangingTo={visibilityChangingTo} />
		</Modal>
	);
}

type ShareClipModalRendererProps = ModalProps<unknown> & {
	clipId: string;
	initialTab?: TabType;
};

function ShareClipModalRenderer({ close, clipId, initialTab }: ShareClipModalRendererProps) {
	const { node: clip, viewer } = useLazyLoadQuery<ShareClipModalQuery>(
		modalQuery,
		{ clipId },
		{ fetchPolicy: "network-only" },
	);

	if (clip?.__typename !== "MeetingRecordingClip") {
		return null;
	}

	return <ShareClipModal close={close} clip={clip} viewer={viewer} initialTab={initialTab} />;
}

const ShareClipModalRendererWithSuspense = withSuspense(<LoadingSpinner />, ShareClipModalRenderer);

export function openShareClipModal(clipId: string, initialTab?: TabType) {
	__globalModalContext?.open(({ close }) => (
		<ShareClipModalRendererWithSuspense close={close} clipId={clipId} initialTab={initialTab} />
	));
}
